import { DataField } from '@toolpad/core';
import { useSearchParams } from 'react-router';
import { useDebounceCallback } from 'usehooks-ts';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { omit } from 'utils/object';
import { sanitizeGridState } from 'utils/user-table-columns';
import {
  loadGridState,
  saveGridState,
  removeLocalStorageItem,
} from 'utils/storage';

export const stateGridStorageKey = 'grid-state';

type GridStateConfig = {
  gridId: string;
  // State version for this specific grid, increment when breaking changes occur
  version?: number;
  resetPage?: boolean;
  // Forces state reload when navigating between CRUD modes (LIST/CREATE/EDIT/SHOW).
  // Without this, after making grid changes and navigating to create/edit/details pages
  // and returning to list, the state wouldn't be applied because
  // the page doesn't re-render, only internal components do.
  refreshKey?: unknown;
  initialPageSize?: number;
  // Set false when the grid mounts asynchronously: call subscribeToGridEvents
  // in the mounted grid's effect and return its cleanup. Do not subscribe twice.
  subscribeOnMount?: boolean;
  initialFields: DataField[];
  allowedFields?: readonly string[];
};

// Persist completed preference changes, not continuous resize/scroll/selection events.
const gridEvents = [
  'columnWidthChange',
  'columnOrderChange',
  'columnVisibilityModelChange',
  'pinnedColumnsChange',
  'filterModelChange',
  'sortModelChange',
  'paginationModelChange',
] as const;

export const useGridState = ({
  gridId,
  refreshKey,
  version = 1,
  allowedFields,
  initialFields,
  resetPage = false,
  initialPageSize = 20,
  subscribeOnMount = true,
}: GridStateConfig) => {
  const [, setSearchParams] = useSearchParams();
  const storageKey = `${stateGridStorageKey}:${gridId}`;
  const apiRef = useGridApiRef();
  const skipSaveRef = useRef(false);

  const initialState = useMemo(() => {
    const saved = loadGridState(storageKey, version);
    if (!saved)
      return resetPage
        ? {
            pagination: {
              paginationModel: { page: 0, pageSize: initialPageSize },
            },
          }
        : undefined;

    // Exclude preferencePanel as it's UI-only state that should not persist
    const state = omit(saved.state, ['preferencePanel']);
    const sanitized = allowedFields
      ? sanitizeGridState(state, allowedFields)
      : state;
    return resetPage
      ? {
          ...sanitized,
          pagination: {
            ...sanitized.pagination,
            paginationModel: {
              page: 0,
              pageSize:
                sanitized.pagination?.paginationModel?.pageSize ??
                initialPageSize,
            },
          },
        }
      : sanitized;
  }, [
    storageKey,
    version,
    refreshKey,
    allowedFields,
    resetPage,
    initialPageSize,
  ]);

  const hasSavedState = useMemo(
    () => !!loadGridState(storageKey, version),
    [storageKey, version, refreshKey]
  );
  const [isDirty, setIsDirty] = useState(hasSavedState);

  useEffect(() => {
    setIsDirty(hasSavedState);
  }, [hasSavedState, storageKey]);

  // Capture the snapshot while the grid is mounted. A scope change or navigation
  // can dispose the API before the debounced write is flushed.
  const persistState = useCallback(
    (state: ReturnType<NonNullable<typeof apiRef.current>['exportState']>) => {
      const stateWithoutPreferences = omit(state, ['preferencePanel']);
      saveGridState(
        storageKey,
        allowedFields
          ? sanitizeGridState(stateWithoutPreferences, allowedFields)
          : stateWithoutPreferences,
        version
      );
    },
    [storageKey, allowedFields, version]
  );
  const saveState = useDebounceCallback(persistState, 300);

  // Persist the final snapshot to its original scope even when leaving immediately.
  useEffect(
    () => () => {
      saveState.flush();
    },
    [saveState]
  );

  const handleGridEvent = useCallback(() => {
    if (skipSaveRef.current || !apiRef.current) return;
    saveState(apiRef.current.exportState());
    setIsDirty(true);
  }, [saveState]);

  const subscribeToGridEvents = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const unsubscribers = gridEvents.map((event) =>
      api.subscribeEvent(event, handleGridEvent)
    );
    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [handleGridEvent, storageKey]);

  const subscriptionRef = useRef<{
    unsubscribe?: () => void;
    api: typeof apiRef.current;
    subscribe: typeof subscribeToGridEvents;
  } | null>(null);

  // Async grids may appear after a later render. Compare in an effect, keeping
  // subscriptions intact until the actual API instance or handler changes.
  useEffect(() => {
    const api = subscribeOnMount ? apiRef.current : null;
    const current = subscriptionRef.current;
    if (current?.api === api && current?.subscribe === subscribeToGridEvents)
      return;
    current?.unsubscribe?.();
    subscriptionRef.current = api
      ? {
          api,
          subscribe: subscribeToGridEvents,
          unsubscribe: subscribeToGridEvents(),
        }
      : null;
  });
  useEffect(
    () => () => {
      subscriptionRef.current?.unsubscribe?.();
      subscriptionRef.current = null;
    },
    []
  );

  const resetState = useCallback(() => {
    if (!apiRef.current) return;

    saveState.cancel();
    skipSaveRef.current = true;
    removeLocalStorageItem(storageKey);

    apiRef.current.restoreState({
      sorting: { sortModel: [] },
      pinnedColumns: { left: [], right: [] },

      filter: { filterModel: { items: [], quickFilterValues: [] } },
      pagination: { paginationModel: { page: 0, pageSize: initialPageSize } },
      columns: {
        columnVisibilityModel: {},
        orderedFields: initialFields.map((f) => f.field),
      },
    });

    apiRef.current.updateColumns(initialFields);

    setIsDirty(false);

    setSearchParams({}, { replace: true });

    setTimeout(() => {
      skipSaveRef.current = false;
    }, 0);
  }, [storageKey, apiRef, initialFields, saveState, initialPageSize]);

  return {
    apiRef,
    version,
    isDirty,
    storageKey,
    resetState,
    initialState,
    subscribeToGridEvents,
  };
};
